import React, { useEffect, useState } from "react";
import { Table, Button, Input, InputNumber, message, Popconfirm } from "antd";
import { db } from "../../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import "./registration.css";

interface Room {
  key: string;
  name: string;
  image: string;
  single: number;
  double: number;
  limitSingle?: number;
  limitDouble?: number;
  bookedSingle?: number;
  bookedDouble?: number;
  remainingSingle?: number;
  remainingDouble?: number;
  booked?: number;
  limit?: number;
  remaining?: number;
  sortOrder?: number;
}

interface Guest {
  roomName: string;
  occupancy: "Single" | "Double";
}

const ManageRooms: React.FC = () => {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [newRoom, setNewRoom] = useState<Room>({
    key: "",
    name: "",
    image: "",
    single: 0,
    double: 0,
    limitSingle: undefined,
    limitDouble: undefined,
    sortOrder: 0,
  });

  const fetchRooms = async () => {
    const roomSnapshot = await getDocs(collection(db, "rooms"));

    const guestSnapshot = await getDocs(collection(db, "hotelBookings"));
    const guests: Guest[] = [];

    for (const bookingDoc of guestSnapshot.docs) {
      const roomName = bookingDoc.data().roomName;

      const guestsCollectionRef = collection(
        db,
        "hotelBookings",
        bookingDoc.id,
        "guests"
      );
      const guestsDocs = await getDocs(guestsCollectionRef);

      guestsDocs.forEach((guestDoc) => {
        const g = guestDoc.data();
        if (g.occupancy === "Single" || g.occupancy === "Double") {
          guests.push({ roomName, occupancy: g.occupancy });
        }
      });
    }

    const data: Room[] = roomSnapshot.docs.map((doc) => {
      const d = doc.data();
      const key = doc.id;
      const roomGuests = guests.filter((g) => g.roomName === d.name);
      const bookedSingle = roomGuests.filter(
        (g) => g.occupancy === "Single"
      ).length;
      const bookedDouble = roomGuests.filter(
        (g) => g.occupancy === "Double"
      ).length;

      const limitSingle = d.limitSingle ?? undefined;
      const limitDouble = d.limitDouble ?? undefined;

      const booked = bookedSingle + bookedDouble;
      const limit = (limitSingle ?? 0) + (limitDouble ?? 0);

      const remainingSingle =
        limitSingle != null
          ? Math.max(limitSingle - bookedSingle, 0)
          : undefined;
      const remainingDouble =
        limitDouble != null
          ? Math.max(limitDouble - bookedDouble, 0)
          : undefined;
      const remaining =
        limitSingle != null && limitDouble != null
          ? Math.max(limit - booked, 0)
          : undefined;

      return {
        key,
        name: d.name,
        image: d.image,
        single: d.single,
        double: d.double,
        limitSingle,
        limitDouble,
        bookedSingle,
        bookedDouble,
        remainingSingle,
        remainingDouble,
        booked,
        limit,
        remaining,
        sortOrder: d.sortOrder ?? 0,
      };
    });

    setRooms(data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const handleAddRoom = async () => {
    if (!newRoom.name || !newRoom.image) {
      message.error("Please fill in all fields.");
      return;
    }
    await addDoc(collection(db, "rooms"), newRoom);
    message.success("Room added successfully");
    setNewRoom({ key: "", name: "", image: "", single: 0, double: 0 });
    fetchRooms();
  };

  const handleEditRoom = (record: Room) => {
    setNewRoom(record);
  };

  const handleUpdateRoom = async () => {
    const roomRef = doc(db, "rooms", newRoom.key);
    await updateDoc(roomRef, {
      name: newRoom.name,
      image: newRoom.image,
      single: newRoom.single,
      double: newRoom.double,
      limitSingle: newRoom.limitSingle ?? null,
      limitDouble: newRoom.limitDouble ?? null,
      sortOrder: newRoom.sortOrder ?? 0,
    });

    message.success("Room updated successfully");
    setNewRoom({ key: "", name: "", image: "", single: 0, double: 0 });
    fetchRooms();
  };

  const handleDeleteRoom = async (key: string) => {
    await deleteDoc(doc(db, "rooms", key));
    message.success("Room deleted");
    fetchRooms();
  };

  const columns = [
    { title: "Sort", dataIndex: "sortOrder", key: "sortOrder" },
    {
      title: "Image",
      key: "image",
      render: (record: Room) => (
        <img src={record.image} alt={record.name} width={100} height={100} />
      ),
    },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Single Rate", dataIndex: "single", key: "single" },
    { title: "Double Rate", dataIndex: "double", key: "double" },
    { title: "Limit (Single)", dataIndex: "limitSingle", key: "limitSingle" },
    {
      title: "Booked (Single)",
      dataIndex: "bookedSingle",
      key: "bookedSingle",
    },
    {
      title: "Remaining (Single)",
      dataIndex: "remainingSingle",
      key: "remainingSingle",
    },
    { title: "Limit (Double)", dataIndex: "limitDouble", key: "limitDouble" },
    {
      title: "Booked (Double)",
      dataIndex: "bookedDouble",
      key: "bookedDouble",
    },
    {
      title: "Remaining (Double)",
      dataIndex: "remainingDouble",
      key: "remainingDouble",
    },
    // { title: "Total Limit", dataIndex: "limit", key: "limit" },
    // { title: "Total Booked", dataIndex: "booked", key: "booked" },
    // { title: "Total Remaining", dataIndex: "remaining", key: "remaining" },
    {
      title: "Actions",
      key: "actions",
      render: (record: Room) => (
        <>
          <Button type="link" onClick={() => handleEditRoom(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDeleteRoom(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h1 className="title" style={{ color: "#2e3c52" }}>
        Manage Rooms
      </h1>
      <div
        className="edit-modal-field"
        style={{ marginBottom: 20, marginTop: 20 }}
      >
        <InputNumber
          placeholder="Sort Order"
          value={newRoom.sortOrder}
          onChange={(value) =>
            setNewRoom({ ...newRoom, sortOrder: value ?? 0 })
          }
          style={{ marginBottom: 10, width: "100%", color: "#000" }}
        />
        <Input
          placeholder="Room Name"
          value={newRoom.name}
          onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
          style={{ marginBottom: 10, color: "#000" }}
        />
        <Input
          placeholder="Image URL"
          value={newRoom.image}
          onChange={(e) => setNewRoom({ ...newRoom, image: e.target.value })}
          style={{ marginBottom: 10, color: "#000" }}
        />
        <InputNumber
          placeholder="Single Rate"
          value={newRoom.single}
          onChange={(value) => setNewRoom({ ...newRoom, single: value || 0 })}
          style={{ marginBottom: 10, width: "100%", color: "#000" }}
        />
        <InputNumber
          placeholder="Double Rate"
          value={newRoom.double}
          onChange={(value) => setNewRoom({ ...newRoom, double: value || 0 })}
          style={{ marginBottom: 10, width: "100%", color: "#000" }}
        />
        <InputNumber
          placeholder="Limit (Single)"
          value={newRoom.limitSingle}
          onChange={(value) =>
            setNewRoom({ ...newRoom, limitSingle: value ?? undefined })
          }
          style={{ marginBottom: 10, width: "100%", color: "#000" }}
        />
        <InputNumber
          placeholder="Limit (Double)"
          value={newRoom.limitDouble}
          onChange={(value) =>
            setNewRoom({ ...newRoom, limitDouble: value ?? undefined })
          }
          style={{ marginBottom: 10, width: "100%", color: "#000" }}
        />
        {newRoom.key ? (
          <Button className="btn btn-register" onClick={handleUpdateRoom}>
            Update Room
          </Button>
        ) : (
          <Button className="btn btn-register" onClick={handleAddRoom}>
            Add Room
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={rooms}
        pagination={{ pageSize: 5 }}
        bordered
      />
    </div>
  );
};

export default ManageRooms;
